/**
 * Owner: Masa Bayu
 * File: AuthorMoreMenu.tsx
 * Date:Time: 2026-10-19 13:20
 * Description: §412 — butang ⋮ hero author dengan menu popover dua tindakan
 *   benar: salin pautan profil (clipboard) dan langganan notifikasi buku
 *   baharu (stor localStorage §386). Tiada butang mati.
 */

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { ReactElement } from 'react';
import type { PublicAuthor } from './public-author-data';
import {
  getAuthorAlertSnapshot,
  subscribeAuthorAlerts,
  toggleAuthorAlert,
} from './public-author-local-stores';
import type { AuthorScreensLabels } from '../discovery/author-discovery-labels';

export interface AuthorMoreMenuProps {
  readonly author: PublicAuthor;
  readonly t: AuthorScreensLabels;
}

export function AuthorMoreMenu({ author, t }: AuthorMoreMenuProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const alerts = useSyncExternalStore(subscribeAuthorAlerts, getAuthorAlertSnapshot);
  const subscribed = alerts.includes(author.id);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const copiedTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onDown = (event: MouseEvent): void => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(
    () => () => {
      if (copiedTimer.current !== null) {
        window.clearTimeout(copiedTimer.current);
      }
    },
    [],
  );

  const copyLink = (): void => {
    const url = `${window.location.origin}/author/${author.id}`;
    void navigator.clipboard?.writeText(url).catch(() => undefined);
    setCopied(true);
    if (copiedTimer.current !== null) {
      window.clearTimeout(copiedTimer.current);
    }
    copiedTimer.current = window.setTimeout(() => {
      setCopied(false);
    }, 1600);
    setOpen(false);
  };

  return (
    <div className="qxapf__more" ref={rootRef}>
      <button
        type="button"
        className="qxapf__more-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t.profile.moreAria}
        onClick={() => {
          setOpen((prev) => !prev);
        }}
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="5" r="1.8" />
          <circle cx="12" cy="12" r="1.8" />
          <circle cx="12" cy="19" r="1.8" />
        </svg>
      </button>
      {open ? (
        <div className="qxapf__menu" role="menu" aria-label={t.profile.moreAria}>
          <button type="button" role="menuitem" className="qxapf__menu-item" onClick={copyLink}>
            {copied ? t.profile.linkCopied : t.profile.copyLink}
          </button>
          <button
            type="button"
            role="menuitem"
            className="qxapf__menu-item"
            aria-pressed={subscribed}
            onClick={() => {
              toggleAuthorAlert(author.id);
            }}
          >
            {subscribed ? '✓ ' : ''}
            {t.profile.alerts}
          </button>
        </div>
      ) : null}
    </div>
  );
}
